import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import type { DestinationAffinityCopyResult } from '~/queries/customerCommunication/requestGetDestinationAffinityCopy';
import type { DestinationAffinityDraftResult } from '~/queries/customerCommunication/requestGetDestinationAffinityDraft';

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import {
  useGenerateDestinationAffinityCopy,
  useGenerateDestinationAffinityDraft,
} from '~/hooks/apis/customer-communication/useDestinationAffinityGeneration';
import useToggleState from '~/hooks/useToggleState';

import { getDestinationAffinityCopyOfferIds } from '../../utils/destinationAffinityUtils';

interface Props {
  brand: string;
  country: string;
  destination: string;
  /** Whether the email has content, which generating replaces after asking to confirm */
  hasExistingContent: boolean;
  onApplyDraft: (draft: DestinationAffinityDraftResult) => void;
  onApplyCopy: (copy: DestinationAffinityCopyResult) => void;
  /** The email's sections as they're displayed now, which regenerated copy is written about */
  getEmailSections: () => Array<CustomerCommunication.CustomScheduleDynamicSection>;
}

function getEmailKey(brand: string, country: string, destination: string) {
  return `${brand}|${country}|${destination}`;
}

function getErrorMessage(error: Error | null) {
  return error?.message || 'Something went wrong';
}

/**
 * Generates a destination affinity email: the draft (hero image, offers ranked by margin and trending destinations) goes
 * in as soon as it's ready, then the AI copy written about its offers. The copy can be regenerated on its own at any
 * time, about the offers in the email as it is then.
 */
function DestinationAffinityGenerator({
  brand,
  country,
  destination,
  hasExistingContent,
  onApplyDraft,
  onApplyCopy,
  getEmailSections,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const draftMutation = useGenerateDestinationAffinityDraft();
  const copyMutation = useGenerateDestinationAffinityCopy();
  const { mutateAsync: generateDraft, reset: resetDraft } = draftMutation;
  const { mutateAsync: generateCopy, reset: resetCopy } = copyMutation;
  const { isToggled: isConfirmOpen, toggleOn: openConfirm, toggleOff: closeConfirm } = useToggleState(false);
  // whether the copy being written is the second step of generating the email, rather than a regeneration
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);

  const canGenerate = !!brand && !!country && !!destination;
  const isBusy = draftMutation.isPending || copyMutation.isPending;
  const steps = [
    { label: 'Adding the hero image and highest-margin offers', done: draftMutation.isSuccess },
    ...(draftMutation.isSuccess
      ? [{ label: 'Writing the subject line and intro, and picking articles (up to 20 seconds)', done: false }]
      : []),
  ];

  // A result only describes the email it was generated for. Requests can't be cancelled, so one still in flight when the
  // brand, country or destination changes is dropped when it lands instead of filling the email for the wrong place.
  const emailKey = getEmailKey(brand, country, destination);
  const currentEmailKey = useRef(emailKey);
  useEffect(() => {
    currentEmailKey.current = emailKey;
    setIsGeneratingEmail(false);
    resetDraft();
    resetCopy();
  }, [emailKey, resetDraft, resetCopy]);

  const writeCopy = useCallback(
    async (offerIds: Array<string>) => {
      if (offerIds.length === 0) {
        enqueueSnackbar('Add offers to the email to write the copy about', { variant: 'warning' });
        return;
      }

      const startedFor = getEmailKey(brand, country, destination);
      let copy: DestinationAffinityCopyResult;
      try {
        copy = await generateCopy({ brand, country, destination, offerIds });
      } catch {
        // shown from the mutation's error state, with a retry
        return;
      }
      if (currentEmailKey.current !== startedFor) {
        return;
      }
      onApplyCopy(copy);
      enqueueSnackbar('Wrote the subject line, intro and articles, ready to review', { variant: 'success' });
    },
    [brand, country, destination, generateCopy, onApplyCopy, enqueueSnackbar],
  );

  const generate = useCallback(async () => {
    closeConfirm();
    resetCopy();
    setIsGeneratingEmail(true);
    const startedFor = getEmailKey(brand, country, destination);
    try {
      let draft: DestinationAffinityDraftResult;
      try {
        draft = await generateDraft({ brand, country, destination });
      } catch {
        // shown from the mutation's error state, with a retry
        return;
      }
      if (currentEmailKey.current !== startedFor) {
        return;
      }
      onApplyDraft(draft);
      // the draft's sections haven't rendered into forms yet, so its offers come from the response
      await writeCopy(getDestinationAffinityCopyOfferIds(draft.draft.customSections));
    } finally {
      // a run for an email the team has moved off mustn't end a newer one's progress
      if (currentEmailKey.current === startedFor) {
        setIsGeneratingEmail(false);
      }
    }
  }, [brand, country, destination, closeConfirm, resetCopy, generateDraft, onApplyDraft, writeCopy]);

  const regenerateCopy = useCallback(() => {
    writeCopy(getDestinationAffinityCopyOfferIds(getEmailSections()));
  }, [writeCopy, getEmailSections]);

  const handleGenerateClick = useCallback(() => {
    if (hasExistingContent) {
      openConfirm();
    } else {
      generate();
    }
  }, [hasExistingContent, openConfirm, generate]);

  return (
    <Stack direction="column" gap={2}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'center' }}
        gap={2}
      >
        <Box>
          <Typography variant="subtitle1">Generate with AI</Typography>
          <Typography variant="body2" color="text.secondary">
            {canGenerate
              ? "Fills the email with this destination's hero image, its highest-margin offers from the last 3 days and trending destinations, then writes the subject line and intro and picks articles to match. Everything stays editable, and the copy can be regenerated after changing the offers."
              : 'Select a country and a destination to generate the email.'}
          </Typography>
        </Box>
        <Stack direction="row" gap={1} flexShrink={0}>
          {canGenerate && hasExistingContent && !isGeneratingEmail && (
            <Button variant="text" disabled={isBusy} loading={copyMutation.isPending} onClick={regenerateCopy}>
              Regenerate copy
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            disabled={!canGenerate || isBusy}
            loading={isGeneratingEmail}
            onClick={handleGenerateClick}
          >
            Generate with AI
          </Button>
        </Stack>
      </Stack>

      {isGeneratingEmail && (
        <Stack direction="column" gap={1}>
          {steps.map((step) => (
            <Stack key={step.label} direction="row" alignItems="center" gap={1}>
              {step.done ? <CheckCircleIcon fontSize="small" color="success" /> : <CircularProgress size={16} />}
              <Typography variant="body2">{step.label}</Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {draftMutation.isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={generate}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Couldn't generate the email</AlertTitle>
          The email hasn't been changed. {getErrorMessage(draftMutation.error)}
        </Alert>
      )}

      {copyMutation.isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={regenerateCopy}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Couldn't write the copy</AlertTitle>
          The subject line, intro and articles weren't changed. {getErrorMessage(copyMutation.error)}
        </Alert>
      )}

      <Dialog open={isConfirmOpen} onClose={closeConfirm} aria-labelledby="destination-affinity-confirm-title">
        <DialogTitle id="destination-affinity-confirm-title">Replace the email content?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This replaces the header image and every email section with generated content, then rewrites the subject
            line, pre-header and intro. Push notifications are kept.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={closeConfirm}>
            Cancel
          </Button>
          <Button variant="contained" onClick={generate}>
            Replace
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export default DestinationAffinityGenerator;
